/**
 * Shared storage/revocation helpers for the partner session token.
 *
 * The token lives under a per-address key and is read by both the partner
 * panel (`usePartnerAuth`) and the main app, which only needs to end the
 * partner session when the wallet is disconnected there.
 */
import { logoutPartner } from './api';

export const tokenKey = (address: string): string => `pyramida_partner_token:${address}`;
const rememberedAddressKey = 'pyramida_partner_remembered_address';
const activeAddressKey = 'pyramida_partner_active_address';

export type PartnerTokenStorage = 'remembered' | 'session';
export interface PartnerStoredSession {
  address: string;
  token: string;
}

const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  return atob(padded);
};

export const tokenExpiryMs = (token: string): number | null => {
  const dot = token.lastIndexOf('.');
  if (dot <= 0) return null;
  try {
    const parsed = JSON.parse(decodeBase64Url(token.slice(0, dot))) as { e?: unknown };
    return typeof parsed.e === 'number' && Number.isFinite(parsed.e) ? parsed.e : null;
  } catch {
    return null;
  }
};

export const tokenExpired = (token: string, now = Date.now()): boolean => {
  const expiresAt = tokenExpiryMs(token);
  return expiresAt === null || expiresAt <= now;
};

function readValidStoredToken(storage: Storage, address: string, now = Date.now()): string | null {
  const key = tokenKey(address);
  const token = storage.getItem(key);
  if (!token) return null;
  if (!tokenExpired(token, now)) return token;
  storage.removeItem(key);
  return null;
}

// Storage policy:
//   • default        → sessionStorage: closing the tab/browser ends the session,
//                       so on a shared computer the next person starts logged out.
//   • "remember me"   → localStorage: the (longer-lived) token survives browser
//                       restarts on the partner's own, trusted device.
export const readToken = (address: string, now = Date.now()): string | null =>
  readValidStoredToken(window.localStorage, address, now) ??
  readValidStoredToken(window.sessionStorage, address, now);

export const tokenStorage = (address: string, now = Date.now()): PartnerTokenStorage | null => {
  if (readValidStoredToken(window.localStorage, address, now)) return 'remembered';
  if (readValidStoredToken(window.sessionStorage, address, now)) return 'session';
  return null;
};

export const readRememberedSession = (now = Date.now()): PartnerStoredSession | null => {
  const rememberedAddress = window.localStorage.getItem(rememberedAddressKey);
  if (rememberedAddress) {
    const token = readValidStoredToken(window.localStorage, rememberedAddress, now);
    if (token) return { address: rememberedAddress, token };
    window.localStorage.removeItem(rememberedAddressKey);
  }

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key?.startsWith('pyramida_partner_token:')) continue;
    const address = key.slice('pyramida_partner_token:'.length);
    const token = readValidStoredToken(window.localStorage, address, now);
    if (token) {
      window.localStorage.setItem(rememberedAddressKey, address);
      return { address, token };
    }
  }

  return null;
};

export const readStoredSession = (now = Date.now()): PartnerStoredSession | null => {
  const activeAddress = window.sessionStorage.getItem(activeAddressKey);
  if (activeAddress) {
    const token = readToken(activeAddress, now);
    if (token) return { address: activeAddress, token };
    window.sessionStorage.removeItem(activeAddressKey);
  }

  for (let i = 0; i < window.sessionStorage.length; i += 1) {
    const key = window.sessionStorage.key(i);
    if (!key?.startsWith('pyramida_partner_token:')) continue;
    const address = key.slice('pyramida_partner_token:'.length);
    const token = readValidStoredToken(window.sessionStorage, address, now);
    if (token) {
      window.sessionStorage.setItem(activeAddressKey, address);
      return { address, token };
    }
  }

  return readRememberedSession(now);
};

export const writeToken = (address: string, token: string, remember: boolean): void => {
  const [target, other] = remember
    ? [window.localStorage, window.sessionStorage]
    : [window.sessionStorage, window.localStorage];
  target.setItem(tokenKey(address), token);
  other.removeItem(tokenKey(address));
  window.sessionStorage.setItem(activeAddressKey, address);
  if (remember) window.localStorage.setItem(rememberedAddressKey, address);
  else if (window.localStorage.getItem(rememberedAddressKey) === address) {
    window.localStorage.removeItem(rememberedAddressKey);
  }
};

export const removeToken = (address: string): void => {
  window.localStorage.removeItem(tokenKey(address));
  window.sessionStorage.removeItem(tokenKey(address));
  if (window.sessionStorage.getItem(activeAddressKey) === address) {
    window.sessionStorage.removeItem(activeAddressKey);
  }
  if (window.localStorage.getItem(rememberedAddressKey) === address) {
    window.localStorage.removeItem(rememberedAddressKey);
  }
};

/** Revoke every partner token issued to the wallet and clear local storage. */
export const revokePartnerSession = (address: string): void => {
  const stored = readToken(address);
  if (stored) void logoutPartner(stored);
  removeToken(address);
};
